import { DeliveryCommunicationFrequency } from '@contracts/order/valueNormalizers/DeliveryCommunicationFrequency';
import type { Client } from '@src/clients/domain/Client';
import { DeliveryTypeOption } from '@src/clients/domain/Client';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { FormTextInput } from '@src/common/components/FormTextInput';
import { Stack } from '@src/common/components/Stack';
import { Toggle } from '@src/common/components/Toggle';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import Config from '@src/config/Config';
import type { DriverListing } from '@src/driver/domain/DriverList';
import { useDrivers } from '@src/driver/hooks/useDrivers';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { fieldI18nKeyFrom } from '@src/orders/PermitFormSections/FieldI18nKeyFrom';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  DriverDeliveryCommunicationFrequency,
  DriverDeliveryField,
  DriverDeliveryNameField,
  DriverDeliverySmsAuthorizationField,
  DriverDeliveryTypeField,
  permitDeliveryAndNotificationsFields,
  PrimaryDeliveryTypeField,
  PrimaryDeliveryTypeValueField,
  SecondaryDeliveryTypeField,
  SecondaryDeliveryTypeValueField,
} from '@src/permitField/PermitDeliveryAndNotificationsFields';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

type Props = { client: Client; fields: string[] } & CollapseListener;

export const PermitDeliveryAndNotificationsSection = (props: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();

  const {
    register,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors, disabled: formDisabled },
  } = useFormContext<FormEntries<string[]>>();

  const [
    primaryDeliveryType,
    secondaryDeliveryType,
    driverDeliveryName,
    driverDelivery,
    driverDeliveryType,
    driverDeliveryCommunicationFrequency,
  ] = useWatch({
    control,
    name: [
      PrimaryDeliveryTypeField.fieldName,
      SecondaryDeliveryTypeField.fieldName,
      DriverDeliveryNameField.fieldName,
      DriverDeliveryField.fieldName,
      DriverDeliveryTypeField.fieldName,
      DriverDeliveryCommunicationFrequency.fieldName,
    ],
  });
  const { drivers, isLoading } = useDrivers(props.client.id);

  const { sectionInError } = useSectionError(
    Object.keys(permitDeliveryAndNotificationsFields),
  );

  const selectedDriver = drivers.drivers.find(
    (driver) => driver.name === driverDeliveryName,
  );

  const existentDriverDeliveryName = driverDeliveryName
    ? (driverDeliveryName as string)
    : undefined;
  const existentDriverDeliveryType = driverDeliveryType
    ? (driverDeliveryType as string)
    : undefined;
  const existentDriverDelivery = driverDelivery
    ? (driverDelivery as string)
    : undefined;
  const driverNameOptions = buildDriverNameOptions(
    drivers.drivers,
    existentDriverDeliveryName,
  );

  const getDeliveryOptionsForSelectedDriver = (
    driver: ReturnType<typeof drivers.drivers.find>,
  ) => {
    if (!driver) return [];
    const options: { value: string; label: string }[] = [];
    if (driver.email) {
      options.push({
        value: DeliveryTypeOption.EMAIL,
        label: `${t('fields.driverDeliveryTypeValue.email')} (${driver.email})`,
      });
    }
    if (driver.phone) {
      options.push({
        value: DeliveryTypeOption.SMS,
        label: `${t('fields.driverDeliveryTypeValue.sms')} (${driver.phone})`,
      });
    }
    return options;
  };

  const getExtraDeliveryOptions = (
    driverDelivery: string,
    driverDeliveryType: string,
  ) => {
    const options: { value: string; label: string }[] = [];
    if (driverDeliveryType === DeliveryTypeOption.SMS) {
      options.push({
        value: driverDeliveryType,
        label: `${t('fields.driverDeliveryTypeValue.sms')} (${driverDelivery})`,
      });
    }
    if (driverDeliveryType === DeliveryTypeOption.EMAIL) {
      options.push({
        value: driverDeliveryType,
        label: `${t('fields.driverDeliveryTypeValue.email')} (${driverDelivery})`,
      });
    }
    return options;
  };

  const optionsForSelectedDriver =
    getDeliveryOptionsForSelectedDriver(selectedDriver);
  const storedSelectionMatchesDriver = driverHasMatchingDeliveryValue(
    selectedDriver,
    existentDriverDeliveryType,
    existentDriverDelivery,
  );
  const shouldOverrideWithStoredSelection =
    !!existentDriverDeliveryType &&
    !!existentDriverDelivery &&
    !storedSelectionMatchesDriver;
  const driverDeliveryTypeOptions = shouldOverrideWithStoredSelection
    ? [
        ...optionsForSelectedDriver.filter(
          (option) => option.value !== existentDriverDeliveryType,
        ),
        ...getExtraDeliveryOptions(
          existentDriverDelivery,
          existentDriverDeliveryType,
        ),
      ]
    : optionsForSelectedDriver;

  const handleDriverNameChange = () => {
    reset({
      ...getValues(),
      [DriverDeliveryTypeField.fieldName]: '',
      [DriverDeliveryField.fieldName]: '',
      [DriverDeliverySmsAuthorizationField.fieldName]: false,
      [DriverDeliveryCommunicationFrequency.fieldName]:
        DeliveryCommunicationFrequency.REAL_TIME,
    });
  };

  const handleDeliveryTypeChange = (value?: string) => {
    setValue(DriverDeliverySmsAuthorizationField.fieldName, false);

    if (!selectedDriver) {
      return;
    }
    const currentDestinationValue =
      value === DeliveryTypeOption.SMS
        ? selectedDriver.phone
        : selectedDriver.email;
    if (currentDestinationValue) {
      setValue(DriverDeliveryField.fieldName, currentDestinationValue);
      return;
    }

    setValue(DriverDeliveryField.fieldName, '');
  };

  const toggleDriverDeliveryFrequency = () => {
    if (
      driverDeliveryCommunicationFrequency ==
      DeliveryCommunicationFrequency.FIRST_THEN_LAST.valueOf()
    ) {
      setValue(
        DriverDeliveryCommunicationFrequency.fieldName,
        DeliveryCommunicationFrequency.REAL_TIME,
      );
    } else {
      setValue(
        DriverDeliveryCommunicationFrequency.fieldName,
        DeliveryCommunicationFrequency.FIRST_THEN_LAST,
      );
    }
  };

  //TODO: this is to ensure smooth transition for existing permit application without the new field
  // Could be removed after every non completed permit application include DriverDeliveryCommunicationFrequency
  useEffect(() => {
    if (
      driverDeliveryCommunicationFrequency === undefined &&
      driverDeliveryName
    ) {
      setValue(
        DriverDeliveryCommunicationFrequency.fieldName,
        DeliveryCommunicationFrequency.REAL_TIME,
      );
    }
  }, [driverDeliveryName, driverDeliveryCommunicationFrequency, setValue]);

  return (
    <EditOrderCollapsible
      title={t('permitDelivery')}
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack direction="row" gap={theme.spacing['3']}>
          <Stack direction="column" height="fit-content" className={fullWidth}>
            <FormDropdown
              disabled={
                formDisabled ||
                isFieldDisabled(PrimaryDeliveryTypeField.fieldName)
              }
              onChange={() => {
                reset({
                  ...getValues(),
                  [PrimaryDeliveryTypeValueField.fieldName]: '',
                });
              }}
              control={control}
              fieldName={PrimaryDeliveryTypeField.fieldName}
              options={PrimaryDeliveryTypeField.options.map((option) => ({
                value: option,
                label: t(`deliveryType.${option}`),
              }))}
            />
          </Stack>

          {primaryDeliveryType &&
            primaryDeliveryType !== DeliveryTypeOption.WEB_DOWNLOAD && (
              <FormTextInput
                disabled={
                  formDisabled ||
                  isFieldDisabled(PrimaryDeliveryTypeValueField.fieldName)
                }
                register={register}
                errors={errors}
                fieldName={PrimaryDeliveryTypeValueField.fieldName}
                label={t(
                  `fields.${fieldI18nKeyFrom(
                    PrimaryDeliveryTypeValueField.fieldName,
                    primaryDeliveryType as DeliveryTypeOption,
                  )}`,
                )}
              />
            )}
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <Stack direction="column" height="fit-content" className={fullWidth}>
            <FormDropdown
              disabled={
                formDisabled ||
                isFieldDisabled(SecondaryDeliveryTypeField.fieldName)
              }
              onChange={() => {
                reset({
                  ...getValues(),
                  [SecondaryDeliveryTypeValueField.fieldName]: '',
                });
              }}
              control={control}
              fieldName={SecondaryDeliveryTypeField.fieldName}
              options={SecondaryDeliveryTypeField.options.map((option) => ({
                value: option,
                label: t(`deliveryType.${option}`),
              }))}
            />
          </Stack>

          {secondaryDeliveryType &&
            secondaryDeliveryType !== DeliveryTypeOption.WEB_DOWNLOAD && (
              <FormTextInput
                disabled={
                  formDisabled ||
                  isFieldDisabled(SecondaryDeliveryTypeValueField.fieldName)
                }
                register={register}
                errors={errors}
                fieldName={SecondaryDeliveryTypeValueField.fieldName}
                label={t(
                  `fields.${fieldI18nKeyFrom(
                    SecondaryDeliveryTypeValueField.fieldName,
                    secondaryDeliveryType as DeliveryTypeOption,
                  )}`,
                )}
              />
            )}
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <Stack direction="column" height="fit-content" className={fullWidth}>
            <FormDropdown
              disabled={
                formDisabled ||
                isLoading ||
                isFieldDisabled(DriverDeliveryNameField.fieldName)
              }
              onChange={handleDriverNameChange}
              control={control}
              fieldName={DriverDeliveryNameField.fieldName}
              options={driverNameOptions}
              label={t('fields.driverDelivery')}
            />
          </Stack>

          {driverDeliveryName && (
            <FormDropdown
              disabled={
                formDisabled ||
                isFieldDisabled(DriverDeliveryTypeField.fieldName)
              }
              onChange={handleDeliveryTypeChange}
              control={control}
              fieldName={DriverDeliveryTypeField.fieldName}
              options={driverDeliveryTypeOptions}
            />
          )}
        </Stack>
        {(driverDeliveryName || driverDeliveryCommunicationFrequency) && (
          <Stack gap={theme.spacing['3']}>
            <Typography variant="body1">
              {t('fields.driverDeliveryCommunicationFrequency')}
            </Typography>
            <Toggle
              checked={
                driverDeliveryCommunicationFrequency ===
                DeliveryCommunicationFrequency.REAL_TIME
              }
              onToggle={toggleDriverDeliveryFrequency}
              label={t(
                `fields.deliveryCommunicationFrequencyValue.${driverDeliveryCommunicationFrequency}`,
              )}
              disabled={formDisabled}
            />
          </Stack>
        )}
        {driverDeliveryType === DeliveryTypeOption.SMS && (
          <ControllerFormCheckbox
            control={control}
            fieldName={DriverDeliverySmsAuthorizationField.fieldName}
            label={
              <Trans
                i18nKey="fields.driverDeliverySmsAuthorization"
                values={{ brand: Config.VITE_BRAND }}
                components={{ strong: <strong style={{ fontWeight: 600 }} /> }}
              />
            }
            disabled={
              formDisabled ||
              isFieldDisabled(DriverDeliverySmsAuthorizationField.fieldName)
            }
          />
        )}
      </Stack>
    </EditOrderCollapsible>
  );
};

const buildDriverNameOptions = (
  drivers: DriverListing[],
  currentName?: string,
) => {
  const availableDrivers = getAvailableDrivers(drivers);
  const hasExtraDriver =
    currentName &&
    !availableDrivers.some((option) => option.value === currentName);
  if (hasExtraDriver) {
    availableDrivers.push({ value: currentName, label: currentName });
  }
  return availableDrivers;
};

const driverHasMatchingDeliveryValue = (
  driver: DriverListing | undefined,
  deliveryType: string | undefined,
  deliveryValue: string | undefined,
) => {
  if (!driver || !deliveryType || !deliveryValue) return false;
  if (deliveryType === DeliveryTypeOption.EMAIL) {
    return driver.email === deliveryValue;
  }
  if (deliveryType === DeliveryTypeOption.SMS) {
    return driver.phone === deliveryValue;
  }
  return false;
};

const getAvailableDrivers = (drivers: DriverListing[]) => {
  return drivers
    .filter((driver) => driver.email || driver.phone)
    .map((driver) => ({ value: driver.name, label: driver.name }));
};

const fullWidth = css({
  flex: 1,
});
